import { useState } from 'react';
import { AlertTriangle, Loader2, Shield } from 'lucide-react';
import { useAuth } from '../../auth/useAuth';
import {
  deleteMyAccount,
  setSupportAccess,
  type MyPurchase,
  type MyReview,
} from '../../lib/apiClient';
import { ShareYourFeedback } from './ShareYourFeedback';
import { Button } from '../ui/button';

/**
 * The dashboard's **Settings** tab, copied from production.
 *
 * Production's three sections, in production's order: Share Your Feedback,
 * Support Access, and the Danger Zone. The first appears only when there is a
 * purchase left to review, exactly as production renders it.
 */
export function SettingsTab({
  supportAccessGrantedAt,
  onSupportAccessChanged,
  purchasesNeedingReview,
  onReviewed,
}: {
  supportAccessGrantedAt: string | null;
  onSupportAccessChanged: (grantedAt: string | null) => void;
  purchasesNeedingReview: MyPurchase[];
  onReviewed: (review: MyReview) => void;
}) {
  const { status, session, signOut } = useAuth();
  const token = status === 'signed-in' ? (session?.access_token ?? null) : null;

  const [pendingSupport, setPendingSupport] = useState(false);
  const [supportFailed, setSupportFailed] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteFailed, setDeleteFailed] = useState(false);

  async function toggleSupport(granted: boolean) {
    if (!token || pendingSupport) return;
    setPendingSupport(true);
    setSupportFailed(false);

    const result = await setSupportAccess(token, granted);
    setPendingSupport(false);

    if (result.state !== 'ok') {
      setSupportFailed(true);
      return;
    }
    onSupportAccessChanged(result.supportAccessGrantedAt);
  }

  async function deleteAccount() {
    if (!token || deleting) return;
    setDeleting(true);
    setDeleteFailed(false);

    const ok = await deleteMyAccount(token);
    if (!ok) {
      setDeleting(false);
      setDeleteFailed(true);
      setConfirmDelete(false);
      return;
    }
    // Production signs out and returns to the front page.
    await signOut();
  }

  return (
    <div className="space-y-8 pt-6" data-testid="dashboard-panel-settings">
      <ShareYourFeedback
        purchasesNeedingReview={purchasesNeedingReview}
        onReviewed={onReviewed}
      />

      {/* Support Access */}
      <section className="p-6 rounded-xl border border-border bg-card space-y-4" data-testid="support-access">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" aria-hidden="true" />
              Support Access
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Grant our support team temporary read access to your account data for
              troubleshooting. You can revoke this at any time.
            </p>
          </div>

          <div className="shrink-0 text-right space-y-2">
            {supportAccessGrantedAt ? (
              <>
                <div className="text-xs text-muted-foreground" data-testid="support-granted-on">
                  Granted {shortDate(supportAccessGrantedAt)}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void toggleSupport(false)}
                  disabled={pendingSupport}
                  data-testid="support-revoke"
                >
                  {pendingSupport ? (
                    <Loader2 className="mr-2 h-3 w-3 animate-spin" aria-hidden="true" />
                  ) : null}
                  Revoke Access
                </Button>
              </>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => void toggleSupport(true)}
                disabled={pendingSupport}
                data-testid="support-grant"
              >
                {pendingSupport ? (
                  <Loader2 className="mr-2 h-3 w-3 animate-spin" aria-hidden="true" />
                ) : null}
                Grant Access
              </Button>
            )}
          </div>
        </div>

        {supportAccessGrantedAt ? (
          <div
            className="text-xs text-sky-400 bg-sky-400/10 border border-sky-400/20 rounded-lg px-3 py-2"
            data-testid="support-active"
          >
            Support access is active. Our team can view your assessment data and results to help
            resolve issues.
          </div>
        ) : null}

        {supportFailed ? (
          <p className="text-sm font-medium" data-testid="support-error" role="status">
            Failed to update support access.
          </p>
        ) : null}
      </section>

      {/* Danger Zone */}
      <section
        className="p-6 rounded-xl border border-destructive/30 bg-card space-y-4"
        data-testid="danger-zone"
      >
        <div>
          <h2 className="text-xl font-semibold text-destructive flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" aria-hidden="true" />
            Danger Zone
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            These actions affect your account permanently. Proceed with care.
          </p>
        </div>

        <div className="flex items-center justify-between gap-4 p-4 rounded-lg border border-destructive/20 bg-destructive/5">
          <div className="min-w-0">
            <div className="text-sm font-medium">Delete Account</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Removes your account and all associated data. Contact support to request permanent
              erasure.
            </div>
          </div>

          <div className="shrink-0">
            {confirmDelete ? (
              <div className="flex items-center gap-2">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => void deleteAccount()}
                  disabled={deleting}
                  data-testid="delete-confirm"
                >
                  {deleting ? (
                    <Loader2 className="mr-2 h-3 w-3 animate-spin" aria-hidden="true" />
                  ) : null}
                  Yes, delete my account
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmDelete(false)}
                  disabled={deleting}
                  data-testid="delete-cancel"
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="border-destructive/30 text-destructive hover:bg-destructive/10"
                onClick={() => setConfirmDelete(true)}
                data-testid="delete-account"
              >
                Delete Account
              </Button>
            )}
          </div>
        </div>

        {deleteFailed ? (
          <p className="text-sm font-medium" data-testid="delete-error" role="status">
            Failed to delete account. Please try again.
          </p>
        ) : null}
      </section>
    </div>
  );
}

/** Production formats this with date-fns as `MMM d, yyyy`. */
function shortDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
